/**
 * pt-BR formatting helpers. Centralized so dates, money, and numbers read the
 * same everywhere and stay locale-correct without scattering Intl calls.
 */

const LOCALE = 'pt-BR';

const dateFmt = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const dateTimeFmt = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const usdFmt = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const intFmt = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

const pctFmt = new Intl.NumberFormat(LOCALE, {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/** Format an ISO date/datetime as a short pt-BR date (e.g. "16 de jun. de 2026"). */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : dateFmt.format(date);
}

/** Format an ISO datetime with time (e.g. "16 de jun. de 2026, 10:42"). */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : dateTimeFmt.format(date);
}

/** Compact relative phrase ("há 5 min", "ontem"). Falls back to a short date. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `há ${hours} h`;

  const days = Math.round(hours / 24);
  if (days === 1) return 'ontem';
  if (days < 7) return `há ${days} dias`;

  return dateFmt.format(date);
}

export function formatUsd(amount: number): string {
  return usdFmt.format(amount);
}

const brlFmt = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

/** Format a centavos amount as BRL ("R$ 19,90") — os preços do billing chegam em centavos. */
export function formatBrlCents(cents: number): string {
  return brlFmt.format(cents / 100);
}

export function formatInt(value: number): string {
  return intFmt.format(value);
}

/** Format a 0..1 ratio as a percentage. */
export function formatPercent(ratio: number): string {
  return pctFmt.format(ratio);
}

/** Human-readable file size: KB under 1 MB, otherwise MB with one decimal. */
export function formatBytes(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(0)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Initials for an avatar fallback ("Ana Souza" -> "AS"). */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}
